import { z } from 'zod';
import { tasksTaskSchema } from './tasks-task.contract.js';
import { tasksTaskLinkHrefSchema } from './tasks-detail.contract.js';
export const tasksFocusCapability = 'tasks.focus.v1';
export const tasksTemplatesCapability = 'tasks.templates.v1';
export const tasksMyDayProjectionCapability = 'tasks.my-day-projection.v1';
export const tasksFocusWindowTypeSchema = z.enum(['daily', 'weekly']);
export const tasksFocusWindowStatusSchema = z.enum(['planning', 'active', 'closed']);
export const tasksFocusLaneSchema = z.enum([
    'inbox', 'today', 'doing', 'waiting', 'blocked', 'done',
]);
export const tasksTaskTemplateSchema = z.strictObject({
    id: z.string().regex(/^[a-z][a-z0-9-]{0,59}$/),
    label: z.string().trim().min(1).max(100),
    title: z.string().trim().min(1).max(240),
    description: z.string().trim().max(10_000).nullable(),
    priority: z.enum(['low', 'normal', 'high', 'urgent']),
    defaultLane: tasksFocusLaneSchema.exclude(['done']),
    estimatedMinutes: z.number().int().nonnegative().nullable(),
    checklistItems: z.array(z.strictObject({
        title: z.string().trim().min(1).max(500),
        required: z.boolean().default(true),
    })).max(50).default([]),
    links: z.array(z.strictObject({
        label: z.string().trim().min(1).max(240),
        href: tasksTaskLinkHrefSchema,
        targetModuleId: z.string().regex(/^[a-z][a-z0-9-]*$/).nullable().default(null),
        targetResourceType: z.string().regex(/^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/).nullable().default(null),
        targetResourceId: z.string().trim().min(1).max(240).nullable().default(null),
    }).superRefine((value, context) => {
        const targetParts = [value.targetModuleId, value.targetResourceType, value.targetResourceId];
        if (targetParts.some((part) => part !== null) && targetParts.some((part) => part === null)) {
            context.addIssue({ code: 'custom', message: 'Template links require every structured target field.' });
        }
    })).max(20).default([]),
});
function isCalendarDate(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (match === null)
        return false;
    const instant = new Date(`${value}T00:00:00.000Z`);
    return Number.isFinite(instant.getTime()) && instant.toISOString().slice(0, 10) === value;
}
export const tasksLocalDateSchema = z.string().refine(isCalendarDate, 'Expected a calendar date.');
export const tasksFocusWindowSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    windowType: tasksFocusWindowTypeSchema,
    startsOn: tasksLocalDateSchema,
    endsOn: tasksLocalDateSchema,
    title: z.string().max(240).nullable(),
    status: tasksFocusWindowStatusSchema,
    revision: z.number().int().positive(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    closedAt: z.string().datetime({ offset: true }).nullable(),
    closedByMembershipId: z.string().uuid().nullable(),
    closeoutNotes: z.string().max(5_000).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const tasksFocusItemSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    windowId: z.string().uuid(),
    taskId: z.string().uuid(),
    task: tasksTaskSchema,
    lane: tasksFocusLaneSchema,
    rank: z.number().int().nonnegative(),
    commitment: z.string().max(1_000).nullable(),
    carryoverCount: z.number().int().nonnegative(),
    completedInWindow: z.boolean(),
    revision: z.number().int().positive(),
    addedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const tasksFocusCountsSchema = z.strictObject({
    total: z.number().int().nonnegative(),
    inbox: z.number().int().nonnegative(),
    today: z.number().int().nonnegative(),
    doing: z.number().int().nonnegative(),
    waiting: z.number().int().nonnegative(),
    blocked: z.number().int().nonnegative(),
    done: z.number().int().nonnegative(),
});
export const tasksFocusSummaryQuerySchema = z.strictObject({
    windowType: tasksFocusWindowTypeSchema.default('daily'),
    startsOn: tasksLocalDateSchema.optional(),
    endsOn: tasksLocalDateSchema.optional(),
});
export const tasksFocusWindowListQuerySchema = z.strictObject({
    windowType: tasksFocusWindowTypeSchema.optional(),
    status: tasksFocusWindowStatusSchema.optional(),
    limit: z.number().int().min(1).max(100).default(20),
});
export const tasksCreateFocusWindowSchema = z.strictObject({
    commandId: z.string().uuid(),
    windowType: tasksFocusWindowTypeSchema.default('daily'),
    startsOn: tasksLocalDateSchema.optional(),
    endsOn: tasksLocalDateSchema.optional(),
    title: z.string().trim().min(1).max(240).nullable().default(null),
});
export const tasksAddFocusItemSchema = z.strictObject({
    commandId: z.string().uuid(),
    taskId: z.string().uuid(),
    lane: tasksFocusLaneSchema.default('today'),
    rank: z.number().int().nonnegative().optional(),
    commitment: z.string().trim().max(1_000).nullable().default(null),
    carryoverCount: z.number().int().nonnegative().default(0),
});
export const tasksUpdateFocusItemSchema = z.strictObject({
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    lane: tasksFocusLaneSchema.optional(),
    rank: z.number().int().nonnegative().optional(),
    commitment: z.string().trim().max(1_000).nullable().optional(),
    carryoverCount: z.number().int().nonnegative().optional(),
}).superRefine((value, context) => {
    if (Object.keys(value).every((key) => ['commandId', 'expectedRevision'].includes(key))) {
        context.addIssue({ code: 'custom', message: 'At least one Focus item field must change.' });
    }
});
export const tasksReorderFocusItemsSchema = z.strictObject({
    commandId: z.string().uuid(),
    items: z.array(z.strictObject({
        id: z.string().uuid(),
        expectedRevision: z.number().int().positive(),
        lane: tasksFocusLaneSchema,
        rank: z.number().int().nonnegative(),
    })).min(1).max(200).superRefine((items, context) => {
        if (new Set(items.map(({ id }) => id)).size !== items.length) {
            context.addIssue({ code: 'custom', message: 'A Focus item can appear only once.' });
        }
    }),
});
export const tasksRemoveFocusItemSchema = z.strictObject({
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(5).max(2_000),
});
export const tasksCloseFocusWindowSchema = z.strictObject({
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    closeoutNotes: z.string().trim().max(5_000).nullable().default(null),
});
export const tasksCompleteFocusItemSchema = z.strictObject({
    taskCommandId: z.string().uuid(),
    itemCommandId: z.string().uuid(),
    expectedTaskRevision: z.number().int().positive(),
    expectedItemRevision: z.number().int().positive(),
    completeForAll: z.boolean().default(false),
});
export const tasksQuickAddFocusTaskSchema = z.strictObject({
    windowCommandId: z.string().uuid(),
    taskCommandId: z.string().uuid(),
    itemCommandId: z.string().uuid(),
    windowType: tasksFocusWindowTypeSchema.default('daily'),
    startsOn: tasksLocalDateSchema.optional(),
    endsOn: tasksLocalDateSchema.optional(),
    windowTitle: z.string().trim().min(1).max(240).nullable().default(null),
    lane: tasksFocusLaneSchema.default('today'),
    commitment: z.string().trim().max(1_000).nullable().default(null),
    title: z.string().trim().min(1).max(240),
    description: z.string().trim().max(10_000).nullable().default(null),
    priority: z.enum(['low', 'normal', 'high', 'urgent']).default('normal'),
    projectId: z.string().uuid().nullable().default(null),
    dueAt: z.string().datetime({ offset: true }).nullable().default(null),
    estimatedMinutes: z.number().int().nonnegative().nullable().default(null),
    blockedReason: z.string().trim().max(240).nullable().default(null),
    blockedDetail: z.string().trim().max(2_000).nullable().default(null),
    waitingOn: z.string().trim().max(500).nullable().default(null),
    nextChaseAt: z.string().datetime({ offset: true }).nullable().default(null),
}).superRefine((value, context) => {
    if (value.lane === 'blocked' && value.blockedReason === null && value.blockedDetail === null) {
        context.addIssue({
            code: 'custom',
            path: ['blockedReason'],
            message: 'A task placed in the blocked lane needs a reason or detail.',
        });
    }
});
export const tasksFocusSummaryResponseSchema = z.strictObject({
    enabled: z.boolean(),
    defaults: z.strictObject({
        windowType: tasksFocusWindowTypeSchema,
        startsOn: tasksLocalDateSchema,
        endsOn: tasksLocalDateSchema,
    }),
    window: tasksFocusWindowSchema.nullable(),
    items: z.array(tasksFocusItemSchema),
    counts: tasksFocusCountsSchema,
});
export const tasksFocusWindowResponseSchema = z.strictObject({
    data: tasksFocusWindowSchema,
    items: z.array(tasksFocusItemSchema),
    counts: tasksFocusCountsSchema,
    idempotent: z.boolean(),
});
export const tasksFocusWindowsResponseSchema = z.strictObject({
    data: z.array(tasksFocusWindowSchema),
});
export const tasksFocusItemResponseSchema = z.strictObject({
    data: tasksFocusItemSchema,
    idempotent: z.boolean(),
});
export const tasksRemoveFocusItemResponseSchema = z.strictObject({
    removedItemId: z.string().uuid(),
    idempotent: z.boolean(),
});
export const tasksCompleteFocusItemResponseSchema = z.strictObject({
    data: tasksFocusItemSchema,
    completed: z.boolean(),
    idempotent: z.boolean(),
});
export const tasksFocusReorderResponseSchema = z.strictObject({
    data: z.array(tasksFocusItemSchema),
    counts: tasksFocusCountsSchema,
    idempotent: z.boolean(),
});
export const tasksQuickAddFocusTaskResponseSchema = z.strictObject({
    window: tasksFocusWindowSchema,
    item: tasksFocusItemSchema,
    idempotent: z.boolean(),
});
export const tasksMyDayProjectionSchema = z.strictObject({
    generatedAt: z.string().datetime({ offset: true }),
    localDate: tasksLocalDateSchema,
    dueSoonThrough: tasksLocalDateSchema,
    timeZone: z.string().trim().min(1).max(100),
    overdue: z.array(tasksTaskSchema),
    dueToday: z.array(tasksTaskSchema),
    dueSoon: z.array(tasksTaskSchema),
    noDueDate: z.array(tasksTaskSchema),
    counts: z.strictObject({
        overdue: z.number().int().nonnegative(),
        dueToday: z.number().int().nonnegative(),
        dueSoon: z.number().int().nonnegative(),
        noDueDate: z.number().int().nonnegative(),
    }),
});
export const tasksMyDayResponseSchema = z.strictObject({ data: tasksMyDayProjectionSchema });
//# sourceMappingURL=tasks-focus.contract.js.map